import { neuerVersuchMoeglich, type Auftrag } from '@awf/shared';
import { type ReactNode, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, type starteNeuenVersuch } from '../api/client.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { ErrorState } from '../ui/states.js';
import type { AuftragsQuelle } from './runs-source.js';
import { useAuftrag } from './use-runs.js';
import stile from './neuer-versuch-knopf.module.css';

/**
 * "Mit denselben Eingaben neu starten" an einem gescheiterten Auftrag (Spec 2026-11-03-retry-button,
 * 8 und 11): Ein Klick legt
 * einen neuen Auftrag mit den gespeicherten Eingaben an, startet ihn und fuehrt dorthin. Lebt
 * schon ein neuer Versuch, steht statt des Knopfs der Link dorthin -- dieselbe Regel wie im Backend.
 */
// An den Client gekoppelt, damit eine Vertragsaenderung dort hier nicht still durchrutscht.
export type StarteNeuenVersuch = typeof starteNeuenVersuch;

function ZumVersuch({ runId }: { runId: string }) {
  return (
    <Link
      className="eds-button eds-button--secondary eds-button--sm"
      to={`/auftraege/${encodeURIComponent(runId)}`}
    >
      Zum neuen Versuch
    </Link>
  );
}

function weg(zustand: ReturnType<typeof useAuftrag>): boolean {
  return (
    zustand.art === 'daten' &&
    (zustand.auftrag === null || zustand.auftrag.ausgeblendetAm !== undefined)
  );
}

/**
 * Beobachtet den direkten Nachfolger und den neuesten Versuch der Kette (Spec 11.2). Der Link zielt
 * auf den neuesten, damit es bei hoechstens zwei Klicks bleibt; ist der weg, auf den Nachfolger.
 * Solange ein Stand laedt oder unbekannt ist, steht der Link. Erst wenn BEIDE ausgeblendet oder dem
 * Server unbekannt sind, kommt der Knopf zurueck -- dieselbe Pruefung wie im Backend
 * (`lebendeVersuchsKandidaten`). Ein gescheiterter Versuch bleibt verlinkt, neu gestartet wird an
 * ihm (Kette, Spec 6.3).
 */
function VersuchAktion({
  quelle,
  runId,
  neuester,
  knopf,
}: {
  quelle: AuftragsQuelle;
  runId: string;
  /** `neuesterVersuchRunId`; fehlt bei Altbestand aus Runde 1. */
  neuester: string | undefined;
  knopf: ReactNode;
}) {
  const nachfolger = useAuftrag(quelle, runId);
  const juengster = useAuftrag(quelle, neuester ?? runId);
  const ersetzbar = weg(nachfolger) && weg(juengster);
  const ziel = neuester && !weg(juengster) ? neuester : runId;
  const inhalt = ersetzbar ? knopf : <ZumVersuch runId={ziel} />;
  return inhalt ? <div className={stile.aktion}>{inhalt}</div> : null;
}

export function NeuerVersuchKnopf({
  auftrag,
  quelle,
  starte,
  fremd,
}: {
  auftrag: Auftrag;
  quelle: AuftragsQuelle;
  starte: StarteNeuenVersuch;
  /** Gesamtansicht (`admin`/`technik`): Das Backend startet nur fuer den eigenen Auftrag. */
  fremd: boolean;
}) {
  const navigate = useNavigate();
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string>();
  // Sperre gegen den Doppelklick, bevor React `disabled` gerendert hat.
  const laeuft = useRef(false);

  const stand = neuerVersuchMoeglich(auftrag);
  if (stand === 'nicht_gescheitert') return null;

  function klick() {
    if (laeuft.current) return;
    laeuft.current = true;
    setSendet(true);
    setFehler(undefined);
    starte(auftrag.runId).then(
      ({ neuerRunId }) => {
        laeuft.current = false;
        setSendet(false);
        navigate(`/auftraege/${encodeURIComponent(neuerRunId)}`);
      },
      (f: unknown) => {
        laeuft.current = false;
        setSendet(false);
        setFehler(f instanceof ApiError ? f.text : ALLGEMEINER_FEHLERTEXT);
      },
    );
  }

  const ohneEingaben = stand === 'ohne_eingaben';
  const knopf = fremd ? null : (
    <>
      <button
        className="eds-button eds-button--primary eds-button--sm"
        type="button"
        disabled={sendet || ohneEingaben}
        onClick={klick}
      >
        Mit denselben Eingaben neu starten
      </button>
      <p className={stile.hinweis}>
        {ohneEingaben
          ? 'Für diesen Auftrag sind keine Eingaben gespeichert. Bitte legen Sie ihn in der Übersicht über „Neuen Auftrag anlegen“ neu an.'
          : 'Der Auftrag startet mit Ihren bisherigen Eingaben neu.'}
      </p>
      {fehler && <ErrorState text={fehler} />}
    </>
  );

  if (auftrag.wiederholungRunId) {
    return (
      <VersuchAktion
        key={auftrag.wiederholungRunId}
        quelle={quelle}
        runId={auftrag.wiederholungRunId}
        neuester={auftrag.neuesterVersuchRunId}
        knopf={knopf}
      />
    );
  }
  return knopf ? <div className={stile.aktion}>{knopf}</div> : null;
}
